import { Consumer } from '~/constants/common/consumer';
import SearchType from '~/constants/common/SearchType';
import { useConsumer } from '~/context/common/StaticContext';
import type { Suggestion } from '~/contracts/common';
import { EventData } from '~/hooks/home/useSearchBarEvents';

function encodeAndStringify(input: any): string {
  return window.btoa(JSON.stringify(input));
}

function removeNonUTF8(str: string) {
  return str.replace(/[^\x00-\x7F]/g, '');
}

export const getHrefWithSource =
  (refSource: Consumer) =>
  (href: string, eventData?: EventData): string => {
    if (!href.includes('/cars')) return href;
    const url = new URL(href, 'https://www.example.com');

    // refSource value is added to SRP mega-srp-user-info analytics event
    url.searchParams.set('refSource', refSource);

    // searchEvent object decoded by SRP
    // added to mega-srp-keyword-suggestion analytics event
    if (eventData)
      url.searchParams.set(
        'searchEvent',
        window.btoa(removeNonUTF8(JSON.stringify(eventData)))
      );

    return url.pathname + url.search;
  };

export const useGetConsumerHref = () => {
  const consumer = useConsumer();

  return getHrefWithSource(consumer);
};

/**
  ----------------- highlightText function output example -----------------------

  Orignal Keyword = "Honda Civic Coup w Honda/Sensing"\

  User Input = "Hon"
  Return highlightedText = "<span class="search-result-bold-phrase">Hon</span>da Civic Coup w <span class="search-result-bold-phrase">Hon</span>da/Sensing"

  User Input = ""
  Return keyword = "Honda Civic Coup Honda/Sensing"
*/
export function highlightText(keyword: string, searchString: string) {
  if (!searchString) return keyword;

  const startTag = '<span class="font-bold">';
  const endTag = '</span>';

  const re = new RegExp(searchString, 'gi');

  return keyword.replace(re, startTag + '$&' + endTag);
}

type NormalizeSearchInputShapeArgs = {
  data: any[];
  searchInputType: string;
  userInput?: string;
};

export function normalizeSearchInputShape({
  data,
  searchInputType,
  userInput = ''
}: NormalizeSearchInputShapeArgs): Suggestion[] {
  if (!data) return [];

  switch (searchInputType) {
    case SearchType.SavedSearch:
      return data.map(savedSearch => {
        const { name, url } = savedSearch;

        return {
          searchType: SearchType.SavedSearch,
          text: name,
          highlightedText: highlightText(name, userInput),
          searchHash: url,
          href: `/cars/filters?cvnaid=${url}`
        };
      });

    case SearchType.RecentSearch:
      return data.map(recentSearch => {
        const searchHash = encodeAndStringify({
          filters: recentSearch.filters
        });

        return {
          ...recentSearch,
          searchType: SearchType.RecentSearch,
          text: recentSearch.text,
          highlightedText: highlightText(recentSearch.text, userInput),
          searchHash,
          href: `/cars/filters?cvnaid=${searchHash}`
        };
      });

    case SearchType.TextSearch:
      return data.map(suggestedSearch => {
        const searchHash = encodeAndStringify({
          filters: suggestedSearch.filters
        });

        return {
          ...suggestedSearch,
          searchType: SearchType.TextSearch,
          text: suggestedSearch.text,
          highlightedText: highlightText(suggestedSearch.text, userInput),
          searchHash,
          href: `/cars/filters?cvnaid=${searchHash}`
        };
      });

    default:
      return [];
  }
}
